import * as turf from '@turf/turf';
import { Polygon, Feature, Point, MultiPolygon, LineString, MultiLineString } from '@turf/turf';

type NearbyFeatureGeometry = Polygon | MultiPolygon | LineString | MultiLineString | Point;
export type PolygonWithCenter = Feature<Polygon, { center: Feature<Point> }>
type NearestPoint = Feature<Point, { distanceMeters: number; direction: number;}>;

export class NearestPointFinder {

    private convertCenterToResponse = (center: Feature<Point>): NearestPoint => {
        return {
            ...center,
            properties: {
                direction: 0,
                distanceMeters: 0
            }
        };
    }


    private booleanContains = ({ referencePolygon, nearbyFeature }: {referencePolygon: PolygonWithCenter; nearbyFeature: Feature<NearbyFeatureGeometry>}): boolean => {
        const polygons = nearbyFeature.geometry.type === 'Polygon' ? [nearbyFeature] : nearbyFeature.geometry.coordinates.map(polygonCoords => turf.polygon(polygonCoords));
        return polygons.some(p => turf.booleanContains(referencePolygon, p));
    }

    findNearestPoint = ({ referencePolygon, nearbyFeature }: {referencePolygon: PolygonWithCenter; nearbyFeature: Feature<NearbyFeatureGeometry>}): NearestPoint => {
        const referenceCenter = referencePolygon.properties.center;

        const isPolygon = ['Polygon', 'MultiPolygon'].includes(nearbyFeature.geometry.type);
        const isLine = ['LineString', 'MultiLineString'].includes(nearbyFeature.geometry.type);

        if (nearbyFeature.geometry.type === 'Point' && turf.booleanPointInPolygon(nearbyFeature as Feature<Point>, referencePolygon)){
            return {
                ...nearbyFeature as Feature<Point>,
                properties: {
                    direction: 0,
                    distanceMeters: 0
                }
            };
        }

        if (nearbyFeature.geometry.type === 'Point'){
            return this.buildPoint({ referencePoint: referenceCenter, nearbyPoint: nearbyFeature as Feature<Point> });
        }

        if (isPolygon && turf.booleanPointInPolygon(referenceCenter, nearbyFeature as Feature<Polygon | MultiPolygon>)){
            return this.convertCenterToResponse(referenceCenter);
        }

        if (isPolygon && (turf.booleanOverlap(referencePolygon, nearbyFeature) || this.booleanContains({ referencePolygon, nearbyFeature }))){
            return this.convertCenterToResponse(referenceCenter);
        }

        const nearbyLineString = isLine ? nearbyFeature as Feature<LineString> : this.polygonToLineStrings(nearbyFeature as Feature<Polygon | MultiPolygon>);

        const closestVertex = turf.nearestPointOnLine(nearbyLineString, referenceCenter.geometry.coordinates);

        if (turf.booleanPointInPolygon(closestVertex.geometry.coordinates, referencePolygon)){
            return this.convertCenterToResponse(referenceCenter);
        }

        return this.buildPoint({ referencePoint: referenceCenter, nearbyPoint: closestVertex });
    }

    private buildPoint = ({ referencePoint, nearbyPoint }: { referencePoint: Feature<Point>; nearbyPoint: Feature<Point>; }): NearestPoint => {
        const distanceMeters = turf.distance(referencePoint, nearbyPoint, { units:  'meters' });

        const rhumbBearing = turf.rhumbBearing(referencePoint, nearbyPoint);

        // convert from -180 to +180 [to] 0 to 360
        const direction = rhumbBearing < 0 ? 360 + rhumbBearing : rhumbBearing;

        return {
            ...nearbyPoint,
            properties: {
                distanceMeters,
                direction
            }
        };
    }

    private polygonToLineStrings = (polygon: Feature<Polygon | MultiPolygon>): Feature<LineString | MultiLineString> => {
        let borderLineStrings = turf.polygonToLineString(polygon);
        if (borderLineStrings.type === 'FeatureCollection'){
            let lines:turf.Position[][] = [];
            for (const feature of borderLineStrings.features){
                if (feature.geometry.type === 'LineString'){
                    lines.push(feature.geometry.coordinates);
                } else if (feature.geometry.type === 'MultiLineString'){
                    lines = [
                        ...lines,
                        ...feature.geometry.coordinates
                    ];
                }
            }
            borderLineStrings = turf.multiLineString(lines);
        }
        return borderLineStrings;
    }
}
